import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { BRAND } from "@/content/site";
import { useLang } from "@/lang";
import { scrollToId } from "@/lib/lenis";

const NAV = [
  { id: "services", key: "services" },
  { id: "how-it-works", key: "how" },
  { id: "equipment", key: "equipment" },
  { id: "service-area", key: "area" },
];

function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div
      className="flex items-center rounded-full border border-cream/25 p-0.5"
      role="group"
      aria-label="Language / Idioma"
    >
      {["en", "es"].map((l) => (
        <button
          key={l}
          type="button"
          data-testid={`lang-toggle-${l}`}
          aria-pressed={lang === l}
          onClick={() => setLang(l)}
          className={`relative rounded-full px-3 py-1 text-[11px] font-semibold tracking-[0.15em] transition-colors duration-300 ${
            lang === l ? "text-navy" : "text-cream/70 hover:text-cream"
          }`}
        >
          {lang === l && (
            <motion.span
              layoutId="lang-pill"
              className="absolute inset-0 rounded-full bg-gold"
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            />
          )}
          <span className="relative z-10">{l.toUpperCase()}</span>
        </button>
      ))}
    </div>
  );
}

export default function Header() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, id) => {
    e.preventDefault();
    scrollToId(id);
  };

  return (
    <header
      data-testid="sticky-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-gold/20 bg-navy/90 backdrop-blur-md"
          : "bg-gradient-to-b from-navy/80 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <a
          href="#top"
          data-testid="header-logo"
          onClick={(e) => go(e, "top")}
          className="flex items-center gap-3"
          aria-label="Equipment Concierge — back to top"
        >
          <img
            src="/logo-crest.png"
            alt="Equipment Concierge EC crest logo"
            className="h-10 w-10 rounded-full object-cover ring-1 ring-gold/60"
          />
          <span className="hidden text-[11px] font-semibold uppercase tracking-[0.35em] text-cream sm:block">
            Equipment Concierge
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              data-testid={`nav-${n.id}`}
              onClick={(e) => go(e, n.id)}
              className="text-[12px] font-medium uppercase tracking-[0.2em] text-cream/80 transition-colors duration-300 hover:text-gold"
            >
              {t.nav[n.key]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle />
          <a
            href={BRAND.phoneHref}
            data-testid="header-call-btn"
            className="hidden items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.15em] text-navy transition-all duration-300 hover:bg-gold-hover sm:inline-flex"
          >
            <Phone className="h-3.5 w-3.5" />
            {t.nav.callShort}
          </a>
          <a
            href={BRAND.phoneHref}
            data-testid="header-call-icon-mobile"
            aria-label="Call 305-582-5282"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gold text-navy transition-transform duration-300 hover:scale-105 sm:hidden"
          >
            <Phone className="h-4 w-4" />
          </a>
        </div>
      </div>
    </header>
  );
}
